import { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { toast } from '../components/Toast';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { VatContext } from '../context/VatContext';
import { useSocket } from '../context/SocketContext';
import {
    initiateCheckout,
    getNearbyStores,
    getDeliveryProvider,
    getStoreStockStatus,
} from '../api/checkout.api';
import api from '../api/index';
import { listAddresses, createAddress } from '../api/address.api';
import AddressForm from '../components/AddressForm';
import AddressCard from '../components/AddressCard';
import PaymentConfirmationModal from '../components/PaymentConfirmationModal';
import GuestCheckoutAccountModal from '../components/GuestCheckoutAccountModal';
import StoreLocatorMap from '../components/StoreLocatorMap';
import { StoreHoursBlock } from '../components/home/StoreHoursBlock';
import DeliveryOptions from '../components/DeliveryOptions';
import DeliveryRangeMap from '../components/DeliveryRangeMap';
import TransitCooldownBanner from '../components/TransitCooldownBanner';
import BoughtTogetherSection from '../components/BoughtTogetherSection';
import CartGiftProgress from '../components/CartGiftProgress';
import GiftSelectionModal from '../components/GiftSelectionModal';
import CouponInput from '../components/CouponInput';
import { resolveAssetUrl } from '../config';
import { getWallet, validateRedemption } from '../api/loyalty.api';
import { getMpesaPaymentStatus } from '../api/payment.api';
import {
    formatPhoneWithPrefix,
    formatApiValidationError,
    isValidKenyanPhone,
    normalizeKenyanPhone,
    KENYA_PHONE_ERROR,
    KENYA_PHONE_HINT,
    KENYA_PHONE_PLACEHOLDER,
} from '../utils/phone';
import { loadGuestCheckout, saveGuestCheckout, clearGuestCheckout } from '../utils/guestCheckoutStorage';

const formatKES = (amount) => 'KES ' + Number(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

const MPESA_POLL_INTERVAL_MS = 5000;
const MPESA_MAX_POLLS = 60; // ~5 minutes

const validateName = (name) => /^[a-zA-Z\s'-]+$/.test(name);
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Payment method definitions
// M-Pesa is kept in the list (and all checkout/STK logic stays in this file)
// but hidden from the UI until we re-enable it. Pesapal is first.
const PAYMENT_METHODS = [
    {
        id: 'pesapal',
        label: 'Pesapal (VISA / Mastercard)',
        sublabel: 'Pay securely via Visa, Mastercard, or M-Pesa through Pesapal',
        icon: '🔒',
        badge: 'Recommended',
        isOnline: true,
    },
    {
        id: 'mosmos',
        label: 'Mosmos Installments',
        sublabel: 'Pay in flexible installments or layaway (+20% deposit required via B2B Gateway)',
        icon: '💼',
        isOnline: true,
    },
    {
        id: 'mpesa',
        label: 'M-Pesa',
        sublabel: 'Pay via M-Pesa STK push — instant confirmation',
        icon: '📱',
        badge: 'Recommended',
        isOnline: true,
        hidden: true,
    },
    {
        id: 'store',
        label: 'Buy from Nearest Store',
        sublabel: 'Walk into a store to purchase — no online order created',
        icon: '🏪',
        isOnline: false,
    },
];

const VISIBLE_PAYMENT_METHODS = PAYMENT_METHODS.filter((p) => !p.hidden);
const DEFAULT_PAYMENT_METHOD = 'pesapal';

// Steps: 1=address, 2=payment method, 3=fulfillment (online only), 4=confirm/place
const STEP_LABELS = ['Your Details', 'Fulfillment', 'Payment', 'Confirm'];

export default function Checkout() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { cart, cartLoading, refreshCart, addToCart } = useContext(CartContext);
    const { customer, isAuthenticated, loading: authLoading, register } = useContext(AuthContext);
    const { vatRate } = useContext(VatContext);
    const { socket } = useSocket();

    // ── Wizard step ───────────────────────────────────────────────────────────
    // 1=address, 2=payment method, 3=fulfillment, 4=confirm, 5=pay-at-shop info
    const [currentStep, setCurrentStep] = useState(() => {
        const s = parseInt(searchParams.get('step') || '1', 10);
        return s >= 1 && s <= 5 ? s : 1;
    });

    // ── Address state ─────────────────────────────────────────────────────────
    const [addresses, setAddresses] = useState([]);
    const [addressesLoading, setAddressesLoading] = useState(false);
    // Initialise from URL; restored to the full object once addresses load
    const [selectedAddressId, setSelectedAddressId] = useState(() => searchParams.get('addr') || null);
    const [selectedAddress, setSelectedAddress] = useState(null);
    // Capture the addr param at mount so fetchAddresses can restore it before the sync effect overwrites it
    const initialAddrFromUrl = useRef(searchParams.get('addr'));
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [savingAddress, setSavingAddress] = useState(false);

    // ── Guest details ─────────────────────────────────────────────────────────
    const [guestDetails, setGuestDetails] = useState(() => {
        const saved = loadGuestCheckout()?.details;
        return {
            firstName: saved?.firstName || '',
            lastName: saved?.lastName || '',
            email: saved?.email || '',
            phone: saved?.phone || '',
        };
    });
    const [guestAddress, setGuestAddress] = useState(() => loadGuestCheckout()?.address || null);
    const [editingGuestAddress, setEditingGuestAddress] = useState(false);
    const [guestErrors, setGuestErrors] = useState({});
    const [showGuestAccountModal, setShowGuestAccountModal] = useState(false);
    const [creatingGuestAccount, setCreatingGuestAccount] = useState(false);

    // ── Payment & fulfillment ─────────────────────────────────────────────────
    const [paymentMethod, setPaymentMethod] = useState(() => {
        const pm = searchParams.get('pm');
        return VISIBLE_PAYMENT_METHODS.some(p => p.id === pm) ? pm : DEFAULT_PAYMENT_METHOD;
    });
    const [mpesaPhone, setMpesaPhone] = useState(() => searchParams.get('phone') || '');
    const [fulfillmentType, setFulfillmentType] = useState(() => {
        const f = searchParams.get('fulfillment');
        return f === 'store_pickup' ? 'store_pickup' : 'delivery';
    });
    const [deliveryProvider, setDeliveryProvider] = useState(() => {
        const p = searchParams.get('provider');
        if (p === 'fargo' || p === 'littlecab' || p === 'manual') return p;
        return 'manual';
    });
    const [useStoreCredit, setUseStoreCredit] = useState(false);
    const [storeCreditAmount, setStoreCreditAmount] = useState(0);
    const [orderNotes, setOrderNotes] = useState('');
    const [placingOrder, setPlacingOrder] = useState(false);

    // ── Loyalty State ───────────────────────────────────────────────────────
    const [loyaltyWallet, setLoyaltyWallet] = useState(null);
    const [useLoyalty, setUseLoyalty] = useState(false);
    const [loyaltyPointsToRedeem, setLoyaltyPointsToRedeem] = useState(0);
    const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
    const [validatingLoyalty, setValidatingLoyalty] = useState(false);

    // ── Mosmos State ────────────────────────────────────────────────────────
    const [mosmosScriptUrl, setMosmosScriptUrl] = useState(null);
    const [mosmosParams, setMosmosParams] = useState(null);
    const [mosmosPeriod, setMosmosPeriod] = useState('30');

    // ── Pesapal State ───────────────────────────────────────────────────────
    const [showPesapalIframe, setShowPesapalIframe] = useState(false);
    const [pesapalRedirectUrl, setPesapalRedirectUrl] = useState(null);

    // ── Store locator ─────────────────────────────────────────────────────────
    const [nearbyStores, setNearbyStores] = useState([]);
    const [storesLoading, setStoresLoading] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [selectedStore, setSelectedStore] = useState(null);
    const [stockStatus, setStockStatus] = useState([]);
    const [stockLoading, setStockLoading] = useState(false);
    const [distanceKm, setDistanceKm] = useState(null);
    const [radiusKm, setRadiusKm] = useState(18);
    const [providerLoading, setProviderLoading] = useState(false);
    const [warehouseLocation, setWarehouseLocation] = useState(null);
    const [fargoEnabled, setFargoEnabled] = useState(false);
    const [littleCabEnabled, setLittleCabEnabled] = useState(false);
    const [manualDeliveryEnabled, setManualDeliveryEnabled] = useState(true);

    // ── Bought together ───────────────────────────────────────────────────────
    const [boughtTogetherItems, setBoughtTogetherItems] = useState([]);

    // ── Free gift ─────────────────────────────────────────────────────────────
    const [giftModalSlab, setGiftModalSlab] = useState(null);

    // ── Order success ─────────────────────────────────────────────────────────
    const checkoutCompleteRef = useRef(false);
    const mpesaPollRef = useRef(null);
    const mpesaPollCountRef = useRef(0);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState('initiating');
    const [currentOrder, setCurrentOrder] = useState(null);

    const stopMpesaPolling = useCallback(() => {
        if (mpesaPollRef.current) {
            clearInterval(mpesaPollRef.current);
            mpesaPollRef.current = null;
        }
        mpesaPollCountRef.current = 0;
    }, []);

    const handleMpesaPaymentSuccess = useCallback((orderNumber) => {
        stopMpesaPolling();
        setPaymentStatus('success');
        if (refreshCart) refreshCart();
        setTimeout(() => {
            setShowPaymentModal(false);
            checkoutCompleteRef.current = true;
            clearGuestCheckout();
            navigate(`/order-confirmation/${orderNumber}`);
        }, 2000);
    }, [navigate, refreshCart, stopMpesaPolling]);

    // ── Sync selections to URL (replace so browser Back still works) ─────────
    useEffect(() => {
        const params = new URLSearchParams();
        if (currentStep !== 1) params.set('step', String(currentStep));
        if (selectedAddressId) params.set('addr', selectedAddressId);
        if (paymentMethod !== DEFAULT_PAYMENT_METHOD) params.set('pm', paymentMethod);
        if (fulfillmentType !== 'delivery') params.set('fulfillment', fulfillmentType);
        if (deliveryProvider && deliveryProvider !== 'manual') params.set('provider', deliveryProvider);
        if (mpesaPhone && mpesaPhone !== '+254') params.set('phone', mpesaPhone);
        setSearchParams(params, { replace: true, preventScrollReset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, selectedAddressId, paymentMethod, fulfillmentType, deliveryProvider, mpesaPhone]);

    // ── Init ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (isAuthenticated) {
            clearGuestCheckout();
            return;
        }
        saveGuestCheckout({ details: guestDetails, address: guestAddress });
    }, [isAuthenticated, guestDetails, guestAddress]);

    useEffect(() => {
        document.title = 'Checkout | Fone Express';
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchAddresses();
            fetchLoyaltyWallet();
        }
    }, [isAuthenticated]);

    const fetchLoyaltyWallet = async () => {
        try {
            const res = await getWallet();
            if (res.data?.isOk) setLoyaltyWallet(res.data.data);
        } catch (err) {
            console.error('Error fetching loyalty wallet:', err);
        }
    };

    const handleApplyLoyalty = async (e) => {
        const points = parseInt(e.target.value) || 0;
        if (points <= 0) {
            setLoyaltyDiscount(0);
            setUseLoyalty(false);
            return;
        }

        const stackMode = loyaltyWallet?.stackWithCoupon || 'allow';
        const hasCoupon = Boolean(cart?.couponCode);
        if (hasCoupon && stackMode !== 'allow') {
            toast.error(
                stackMode === 'block_if_coupon'
                    ? 'Loyalty points cannot be used when a coupon is applied'
                    : 'Remove the coupon to redeem loyalty points (only one discount is allowed)'
            );
            return;
        }

        setValidatingLoyalty(true);
        try {
            const res = await validateRedemption(points);
            if (res.data?.isOk) {
                setLoyaltyDiscount(res.data.data.discount);
                setLoyaltyPointsToRedeem(points);
                setUseLoyalty(true);
                toast.success('Loyalty points applied successfully!');
            } else {
                setLoyaltyDiscount(0);
                setUseLoyalty(false);
                toast.error(res.data?.message || 'Invalid points amount');
            }
        } catch (err) {
            setLoyaltyDiscount(0);
            setUseLoyalty(false);
            toast.error(err.response?.data?.message || 'Failed to validate points');
        } finally {
            setValidatingLoyalty(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated && guestDetails.phone === '') {
            setGuestDetails(prev => ({ ...prev, phone: '+254' }));
        }
    }, [isAuthenticated, guestDetails.phone]);

    useEffect(() => {
        // Only auto-fill when no phone was restored from the URL
        const hasUrlPhone = Boolean(searchParams.get('phone'));
        if (hasUrlPhone) return;
        if (isAuthenticated && customer?.phone) {
            const formatted = formatPhoneWithPrefix(
                normalizeKenyanPhone(customer.phone),
            );
            if (mpesaPhone !== formatted) setMpesaPhone(formatted);
        } else if (!isAuthenticated && guestDetails.phone) {
            if (mpesaPhone !== guestDetails.phone) setMpesaPhone(guestDetails.phone);
        } else if (!mpesaPhone) {
            setMpesaPhone('+254');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, customer, guestDetails.phone]);

    useEffect(() => {
        if (checkoutCompleteRef.current) return;
        if (!cartLoading && (!cart || !cart.items || cart.items.length === 0)) {
            navigate('/cart');
        }
    }, [cart, cartLoading, navigate]);

    // Clear loyalty when coupon conflicts with stackWithCoupon rules
    useEffect(() => {
        const stackMode = loyaltyWallet?.stackWithCoupon || 'allow';
        if (stackMode === 'allow') return;
        if (!cart?.couponCode || !useLoyalty) return;
        setUseLoyalty(false);
        setLoyaltyPointsToRedeem(0);
        setLoyaltyDiscount(0);
        toast.info(
            stackMode === 'block_if_coupon'
                ? 'Loyalty points removed — not allowed with a coupon'
                : 'Loyalty points removed — only coupon or loyalty can apply'
        );
    }, [cart?.couponCode, loyaltyWallet?.stackWithCoupon, useLoyalty]);

    // Fetch bought-together items from all cart products
    useEffect(() => {
        if (cartLoading || !cart?.items?.length) return;
        const productIds = [...new Set(
            cart.items.map(i => i.product?._id).filter(Boolean)
        )];
        if (!productIds.length) return;
        api.get('/api/v1/public/products/bought-together', { params: { productIds: productIds.join(',') } })
            .then(res => {
                if (res.data?.isOk) setBoughtTogetherItems(res.data.data || []);
            })
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartLoading, cart?.items?.length]);

    // ── M-Pesa: poll payment status (webhook updates server; no WebSocket required) ──
    useEffect(() => {
        if (!showPaymentModal || paymentMethod !== 'mpesa' || !currentOrder?._id) return;

        const orderId = currentOrder._id;
        const orderNumber = currentOrder.orderNumber;

        const checkMpesaStatus = async () => {
            try {
                const res = await getMpesaPaymentStatus(orderId);
                if (!res.data?.isOk || !res.data?.data) return;

                const { status, orderStatus } = res.data.data;

                if (status === 'paid' || orderStatus === 'confirmed') {
                    handleMpesaPaymentSuccess(orderNumber);
                    return;
                }

                if (status === 'failed' || orderStatus === 'payment_failed') {
                    stopMpesaPolling();
                    setPaymentStatus('failed');
                }
            } catch (err) {
                console.error('M-Pesa status poll error:', err);
            }

            mpesaPollCountRef.current += 1;
            if (mpesaPollCountRef.current >= MPESA_MAX_POLLS) {
                stopMpesaPolling();
            }
        };

        checkMpesaStatus();
        mpesaPollRef.current = setInterval(checkMpesaStatus, MPESA_POLL_INTERVAL_MS);

        return () => stopMpesaPolling();
    }, [
        showPaymentModal,
        paymentMethod,
        currentOrder?._id,
        currentOrder?.orderNumber,
        handleMpesaPaymentSuccess,
        stopMpesaPolling,
    ]);

    // ── Socket (Pesapal only) ─────────────────────────────────────────────────
    useEffect(() => {
        if (!socket || paymentMethod !== 'pesapal') return;

        const onSuccess = (data) => {
            setPaymentStatus('success');
            if (refreshCart) refreshCart();
            setTimeout(() => {
                setShowPaymentModal(false);
                setShowPesapalIframe(false);
                setPesapalRedirectUrl(null);
                checkoutCompleteRef.current = true;
                clearGuestCheckout();
                navigate(`/order-confirmation/${data.orderNumber}`);
            }, 2000);
        };

        const onFailed = () => setPaymentStatus('failed');

        socket.on('payment:success', onSuccess);
        socket.on('payment:failed', onFailed);

        return () => {
            socket.off('payment:success', onSuccess);
            socket.off('payment:failed', onFailed);
        };
    }, [socket, navigate, paymentMethod, refreshCart]);

    // ── Mosmos Script Loader ────────────────────────────────────────────────
    useEffect(() => {
        if (!mosmosScriptUrl || !mosmosParams) return;
        
        const loadMosmos = () => {
            try {
                // eslint-disable-next-line no-undef
                if (typeof Mosmos !== 'undefined') {
                    // eslint-disable-next-line no-undef
                    new Mosmos(mosmosParams);
                    // The script attaches its rendering logic to window load event, manually trigger it
                    window.dispatchEvent(new Event('load'));

                    // Automatically open the modal by simulating a click on the container
                    setTimeout(() => {
                        const btn = document.getElementById('mmBtn');
                        if (btn) btn.click();
                    }, 300);
                } else {
                    toast.error("Failed to load Mosmos payment gateway.");
                }
            } catch (err) {
                console.error("Mosmos initialization error:", err);
                toast.error("Failed to inject Mosmos logic.");
            }
        };

        if (!document.getElementById('mosmos-script')) {
            const script = document.createElement('script');
            script.id = 'mosmos-script';
            script.src = mosmosScriptUrl;
            script.async = true;
            script.onload = loadMosmos;
            script.onerror = () => toast.error("Payment system temporarily unavailable. Please try again or select another method.");
            document.body.appendChild(script);
        } else {
            loadMosmos();
        }
    }, [mosmosScriptUrl, mosmosParams]);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const fetchAddresses = async () => {
        setAddressesLoading(true);
        try {
            const res = await listAddresses();
            if (res.data?.isOk && res.data?.data) {
                const addrList = res.data.data;
                setAddresses(addrList);

                // Prefer the address that was in the URL when the page loaded
                if (initialAddrFromUrl.current) {
                    const fromUrl = addrList.find(a => a._id === initialAddrFromUrl.current);
                    initialAddrFromUrl.current = null;
                    if (fromUrl) {
                        setSelectedAddressId(fromUrl._id);
                        setSelectedAddress(fromUrl);
                        return;
                    }
                }

                const def = addrList.find(a => a.isDefault) || addrList[0];
                if (def) {
                    setSelectedAddressId(def._id);
                    setSelectedAddress(def);
                }
            }
        } catch {
            toast.error('Failed to load saved addresses');
        } finally {
            setAddressesLoading(false);
        }
    };

    const handleSaveNewAddress = async (addressData) => {
        setSavingAddress(true);
        try {
            const res = await createAddress({
                ...addressData,
                phone: normalizeKenyanPhone(addressData.phone),
            });
            if (res.data?.isOk) {
                toast.success('Address saved');
                setShowAddressForm(false);
                await fetchAddresses();
                if (res.data?.data?._id) setSelectedAddressId(res.data.data._id);
            } else {
                toast.error(formatApiValidationError(res.data, 'Failed to save address'));
            }
        } catch (err) {
            toast.error(formatApiValidationError(err, 'Failed to save address'));
        } finally {
            setSavingAddress(false);
        }
    };

    const handleGuestDetailChange = (field, value) => {
        let v = value;
        if (field === 'firstName' || field === 'lastName') v = value.replace(/[^a-zA-Z\s'-]/g, '');
        if (field === 'phone') v = formatPhoneWithPrefix(value);
        setGuestDetails(prev => ({ ...prev, [field]: v }));
        if (guestErrors[field]) setGuestErrors(prev => ({ ...prev, [field]: '' }));
    };

    const validateGuestDetails = () => {
        const errs = {};
        if (!guestDetails.firstName.trim()) errs.firstName = 'First name is required';
        else if (!validateName(guestDetails.firstName)) errs.firstName = 'First name can only contain letters';
        if (!guestDetails.lastName.trim()) errs.lastName = 'Last name is required';
        else if (!validateName(guestDetails.lastName)) errs.lastName = 'Last name can only contain letters';
        if (!guestDetails.email.trim()) errs.email = 'Email is required';
        else if (!validateEmail(guestDetails.email)) errs.email = 'Please enter a valid email address';
        if (!guestDetails.phone.trim() || guestDetails.phone.trim() === '+254') {
            errs.phone = 'Phone number is required';
        } else if (!isValidKenyanPhone(guestDetails.phone)) {
            errs.phone = KENYA_PHONE_ERROR;
        }
        setGuestErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleGuestAddressSubmit = (addressData) => {
        setGuestAddress(addressData);
        setEditingGuestAddress(false);
        toast.success('Address saved');
    };

    const proceedToFulfillmentStep = () => {
        setShowGuestAccountModal(false);
        setCurrentStep(2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const coords = getAddressCoords();
        if (coords) {
            setUserLocation(coords);
            loadDeliveryProvider(coords.lat, coords.lng);
        }
    };

    const handleContinueToFulfillmentStep = () => {
        if (isAuthenticated) {
            if (!selectedAddressId) { toast.error('Please select a shipping address'); return; }
            proceedToFulfillmentStep();
            return;
        }
        if (!validateGuestDetails()) { toast.error('Please fill in your details'); return; }
        if (!guestAddress) { toast.error('Please fill in your shipping address'); return; }
        setShowGuestAccountModal(true);
    };

    const handleContinueAsGuest = () => {
        proceedToFulfillmentStep();
    };

    const handleCreateAccountFromCheckout = async (password) => {
        setCreatingGuestAccount(true);
        try {
            const result = await register({
                firstName: guestDetails.firstName.trim(),
                lastName: guestDetails.lastName.trim(),
                email: guestDetails.email.trim(),
                phone: normalizeKenyanPhone(guestDetails.phone),
                password,
            });
            if (!result?.isOk) {
                toast.error(result?.message || 'Could not create account');
                return;
            }

            try {
                const res = await createAddress({
                    fullName: guestAddress.fullName,
                    phone: normalizeKenyanPhone(guestAddress.phone),
                    addressLine1: guestAddress.addressLine1,
                    addressLine2: guestAddress.addressLine2 || undefined,
                    countryId: guestAddress.countryId,
                    stateId: guestAddress.stateId,
                    cityId: guestAddress.cityId,
                    postalCode: guestAddress.postalCode,
                    label: guestAddress.label || 'home',
                    ...(guestAddress.location?.latitude && guestAddress.location?.longitude
                        ? { location: guestAddress.location }
                        : {}),
                });
                if (res.data?.isOk && res.data?.data?._id) {
                    setSelectedAddressId(res.data.data._id);
                    setSelectedAddress(res.data.data);
                }
            } catch (addrErr) {
                toast.error(formatApiValidationError(addrErr, 'Account created, but the address could not be saved. Add it on the next screen.'));
            }

            await fetchAddresses();
            await refreshCart?.();
            toast.success('Account created. You are now signed in.');
            proceedToFulfillmentStep();
        } catch (err) {
            toast.error(formatApiValidationError(err, 'Could not create account'));
        } finally {
            setCreatingGuestAccount(false);
        }
    };

    const getAddressCoords = () => {
        if (isAuthenticated && selectedAddress) {
            const loc = selectedAddress.location;
            if (loc?.latitude && loc?.longitude) return { lat: loc.latitude, lng: loc.longitude };
        }
        if (!isAuthenticated && guestAddress?.location) {
            const loc = guestAddress.location;
            if (loc?.latitude && loc?.longitude) return { lat: loc.latitude, lng: loc.longitude };
        }
        return null;
    };

    const handleSelectPaymentAndContinue = async () => {
        if (paymentMethod === 'store') {
            // Pay at shop — skip order creation, go to store locator info screen
            setCurrentStep(5);
            loadNearbyStores();
            return;
        }
        if (paymentMethod === 'mosmos') {
            if (!mosmosPeriod || parseInt(mosmosPeriod) <= 0) {
                toast.error('Please enter a valid Mosmos installment period.');
                return;
            }
            
            const depositAmount = Math.round(grandTotal * 0.2);
            // NOTE: the old "sandbox.mosmosbusiness.com" subdomain no longer
            // resolves (ERR_NAME_NOT_RESOLVED). The widget is served from the
            // main domain. Override via VITE_MOSMOS_SCRIPT_URL if Mosmos changes it.
            const mosmosBase = import.meta.env.VITE_MOSMOS_SCRIPT_URL || 'https://mosmosbusiness.com/js/mosmossmartv1.js';
            const mosmosKey = 'eyJpdiI6IktWa2dhRHhsRDhHczBiL1V0VWZ0dXc9PSIsInZhbHVlIjoiVUFBcEFaMXdLL0c4eG5ScTlnVFJ5dz09IiwibWFjIjoiMzg5ZTBlYjZkZDQwZTI5Y2I2MTc3YTFiMmY5MTAyZjgwZjY2NDJhMzllZWM2MWIwODIwOWYwZjQwZmViNTU0NCJ9';
            setMosmosScriptUrl(`${mosmosBase}?secret_key=${mosmosKey}`);
            setMosmosParams({
                name: `E-commerce Order`,
                mmis_in_stock: true,
                price: grandTotal,
                mmdeposit: depositAmount,
                mmperiod: parseInt(mosmosPeriod)
            });
            return;
        }
        // Online payment — go to confirm step
        setCurrentStep(4);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const loadDeliveryProvider = async (lat, lng) => {
        setProviderLoading(true);
        try {
            const res = await getDeliveryProvider(lat, lng);
            if (res.data?.isOk) {
                const {
                    provider,
                    distanceKm: dist,
                    radiusKm: radius,
                    warehouseLocation: wLoc,
                    fargoEnabled: fargoOn,
                    littleCabEnabled: lcOn,
                    manualDeliveryEnabled: manualOn,
                } = res.data.data;
                const fargoAllowed = fargoOn === true;
                const lcAllowed = lcOn === true;
                const manualAllowed = manualOn !== false;
                setFargoEnabled(fargoAllowed);
                setLittleCabEnabled(lcAllowed);
                setManualDeliveryEnabled(manualAllowed);
                setDistanceKm(dist);
                setRadiusKm(radius);
                setWarehouseLocation(wLoc);
                if (provider) {
                    setDeliveryProvider(provider);
                } else if (manualAllowed) {
                    setDeliveryProvider('manual');
                } else if (lcAllowed) {
                    setDeliveryProvider('littlecab');
                } else if (fargoAllowed) {
                    setDeliveryProvider('fargo');
                } else {
                    setDeliveryProvider(null);
                }
            }
        } catch {
            setDeliveryProvider((prev) => prev || (manualDeliveryEnabled ? 'manual' : null));
        } finally {
            setProviderLoading(false);
        }
    };

    const loadNearbyStores = async (lat, lng) => {
        const coords = lat && lng ? { lat, lng } : getAddressCoords();
        if (!coords) return;
        setStoresLoading(true);
        try {
            const res = await getNearbyStores(coords.lat, coords.lng);
            if (res.data?.isOk) {
                setNearbyStores(res.data.data || []);
                setRadiusKm(res.data.meta?.radiusKm || 15);
                if (!userLocation) setUserLocation(coords);
            }
        } catch {
            toast.error('Failed to load nearby stores');
        } finally {
            setStoresLoading(false);
        }
    };

    const handleSelectStore = async (store) => {
        setSelectedStore(store);
        // Check stock status for transit cooldown banner
        const variantIds = (cart?.items || []).map(i => i.variantId).filter(Boolean);
        if (!variantIds.length) return;
        setStockLoading(true);
        try {
            const res = await getStoreStockStatus(store._id, variantIds);
            if (res.data?.isOk) {
                const items = res.data.data.items.map((item, idx) => ({
                    ...item,
                    name: cart?.items?.[idx]?.product?.name || item.variantId,
                }));
                setStockStatus(items);
            }
        } catch {
            setStockStatus([]);
        } finally {
            setStockLoading(false);
        }
    };

    const handleRequestGeolocation = () => {
        if (!navigator.geolocation) {
            toast.error('Geolocation is not supported by your browser. Please enter your location manually.');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                setUserLocation({ lat: latitude, lng: longitude });
                loadNearbyStores(latitude, longitude);
                loadDeliveryProvider(latitude, longitude);
            },
            () => {
                toast.error('Location access denied. Please enter your address details.');
                // Fall back to address coords
                loadNearbyStores();
                const coords = getAddressCoords();
                if (coords) loadDeliveryProvider(coords.lat, coords.lng);
            }
        );
    };

    // Restore maps/provider on refresh if in fulfillment step
    useEffect(() => {
        if (currentStep === 2 && !warehouseLocation && !providerLoading) {
            const coords = getAddressCoords();
            if (coords) {
                setUserLocation(coords);
                loadDeliveryProvider(coords.lat, coords.lng);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, selectedAddress, guestAddress, warehouseLocation, providerLoading]);

     const handleContinueToPaymentStep = () => {
        if (fulfillmentType === 'store_pickup' && !selectedStore) {
            toast.error('Please select a store for pickup');
            return;
        }
        if (fulfillmentType === 'delivery' && !deliveryProvider) {
            toast.error('No delivery option is available. Please choose store pickup or enable a delivery method.');
            return;
        }
        if (
            fulfillmentType === 'delivery' &&
            deliveryProvider === 'littlecab' &&
            (!littleCabEnabled || (distanceKm != null && distanceKm > radiusKm))
        ) {
            toast.error('Little Cab is not available for your address. Please choose Delivery or store pickup.');
            return;
        }
        setCurrentStep(3);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleContinueToConfirm = () => {
        setCurrentStep(4);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePlaceOrder = async () => {
        if (placingOrder) return;

        if (paymentMethod === 'mpesa') {
            if (!mpesaPhone) { toast.error('Please enter your M-PESA phone number'); return; }
            if (!isValidKenyanPhone(mpesaPhone)) {
                toast.error(KENYA_PHONE_ERROR);
                return;
            }
        }

        setPlacingOrder(true);
        try {
            const payload = {
                paymentMethod,
                notes: orderNotes || undefined,
                fulfillmentType,
            };

            if (isAuthenticated) {
                payload.shippingAddressId = selectedAddressId;
            } else {
                payload.guestDetails = {
                    firstName: guestDetails.firstName,
                    lastName: guestDetails.lastName,
                    email: guestDetails.email,
                    phone: normalizeKenyanPhone(guestDetails.phone),
                    shippingAddress: guestAddress,
                };
            }

            if (fulfillmentType === 'delivery') {
                payload.deliveryProvider = deliveryProvider;
            } else if (fulfillmentType === 'store_pickup' && selectedStore) {
                payload.pickupStoreId = selectedStore._id;
            }

            if (useStoreCredit && storeCreditAmount > 0) {
                payload.storeCreditApplied = storeCreditAmount;
            }

            if (useLoyalty && loyaltyPointsToRedeem > 0) {
                payload.loyaltyPointsApplied = loyaltyPointsToRedeem;
            }

            if (paymentMethod === 'mpesa') {
                payload.mpesaPhone = normalizeKenyanPhone(mpesaPhone);
            }

            const res = await initiateCheckout(payload);

            if (res.data?.isOk) {
                const order = res.data.data;
                setCurrentOrder(order);

                if (paymentMethod === 'mpesa') {
                    setShowPaymentModal(true);
                    setPaymentStatus('waiting');
                } else if (paymentMethod === 'pesapal') {
                    if (socket) socket.emit('join:order', { orderNumber: order.orderNumber });
                    setPesapalRedirectUrl(order.paymentAction.redirectUrl);
                    setShowPesapalIframe(true);
                } else {
                    // Visa / Mastercard — order created, pending bank processing
                    checkoutCompleteRef.current = true;
                    clearGuestCheckout();
                    if (refreshCart) refreshCart();
                    navigate(`/order-confirmation/${order.orderNumber}`);
                }
            } else {
                toast.error(res.data?.message || 'Failed to place order');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setPlacingOrder(false);
        }
    };

    const handleClosePaymentModal = () => {
        stopMpesaPolling();
        setShowPaymentModal(false);
        setCurrentOrder(null);
        setPaymentStatus('initiating');
        checkoutCompleteRef.current = false;
    };

    // ── Derived ───────────────────────────────────────────────────────────────
    const customerStoreCredit = customer?.storeCredit || 0;
    const grandTotal = cart?.summary?.total || 0;
    const appliedLoyaltyDiscount = useLoyalty ? loyaltyDiscount : 0;
    const loyaltyStackMode = loyaltyWallet?.stackWithCoupon || 'allow';
    const hasCouponOnCart = Boolean(cart?.couponCode);
    const loyaltyBlockedByCoupon =
        hasCouponOnCart && loyaltyStackMode !== 'allow';
    const couponBlockedByLoyalty =
        useLoyalty &&
        loyaltyDiscount > 0 &&
        loyaltyStackMode === 'loyalty_or_coupon';
    const subtotalAfterLoyalty = Math.max(0, grandTotal - appliedLoyaltyDiscount);
    const appliedCredit = useStoreCredit ? Math.min(customerStoreCredit, subtotalAfterLoyalty) : 0;
    const remainingToPay = Math.max(0, grandTotal - appliedLoyaltyDiscount - appliedCredit);

    useEffect(() => { setStoreCreditAmount(appliedCredit); }, [appliedCredit]);

    const items = cart?.items || [];
    const summary = cart?.summary || {};

    // ── Step indicator labels (1-4 for online; pay-at-shop skips to 5) ────────
    const stepLabels = ['Your Details', 'Fulfillment', 'Payment Method', 'Confirm Order'];

    if (authLoading || cartLoading) {
        return (
            <div className="min-h-screen bg-muted pt-[10px]">
                <div className="max-w-[1400px] mx-auto px-4 py-8 flex items-center justify-center py-20">
                    <div className="text-center">
                        <div className="w-12 h-12 border-4 border-border border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Preparing checkout...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-muted pt-[10px]">
            <div className="max-w-[1400px] mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-2 text-sm mb-6 flex-wrap">
                    <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">Home</Link>
                    <i className="ri-arrow-right-s-line text-muted-foreground/60"></i>
                    <Link to="/cart" className="text-muted-foreground hover:text-primary transition-colors">Cart</Link>
                    <i className="ri-arrow-right-s-line text-muted-foreground/60"></i>
                    <span className="text-foreground font-medium">Checkout</span>
                </nav>

                <h1 className="text-2xl font-bold text-foreground mb-6">Checkout</h1>

                {/* Step Indicators (steps 1–4 only; step 5 = pay-at-shop info) */}
                {currentStep <= 4 && (
                    <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
                        {stepLabels.map((label, idx) => {
                            const stepNum = idx + 1;
                            const isActive = currentStep === stepNum;
                            const isDone = currentStep > stepNum;
                            return (
                                <div key={stepNum} className="flex items-center gap-2 flex-shrink-0">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isDone ? 'bg-green-500 text-white' : isActive ? 'bg-primary text-white' : 'bg-border text-muted-foreground'}`}>
                                        {isDone ? <i className="ri-check-line"></i> : stepNum}
                                    </div>
                                    <span className={`text-sm font-medium ${isActive || isDone ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
                                    {idx < stepLabels.length - 1 && (
                                        <div className={`w-8 h-0.5 ${isDone ? 'bg-green-500' : 'bg-border'}`}></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* ── Main Content ─────────────────────────────────────── */}
                    <div className="lg:col-span-2">

                        {/* ── STEP 1: User Details & Address ─────────────── */}
                        {currentStep === 1 && (
                            <div>
                                {isAuthenticated ? (
                                    <div>
                                        <div className="flex items-center justify-between mb-4">
                                            <h2 className="text-lg font-bold text-foreground">Select Shipping Address</h2>
                                            <button
                                                onClick={() => setShowAddressForm(!showAddressForm)}
                                                className="flex items-center gap-1.5 text-sm text-primary font-medium hover:underline"
                                            >
                                                <i className={showAddressForm ? 'ri-close-line' : 'ri-add-line'}></i>
                                                {showAddressForm ? 'Cancel' : 'Add New Address'}
                                            </button>
                                        </div>

                                        {showAddressForm && (
                                            <div className="mb-6">
                                                <AddressForm
                                                    onSubmit={handleSaveNewAddress}
                                                    onCancel={() => setShowAddressForm(false)}
                                                    isLoading={savingAddress}
                                                    submitLabel="Save & Use This Address"
                                                />
                                            </div>
                                        )}

                                        {addressesLoading ? (
                                            <div className="flex items-center justify-center py-12">
                                                <div className="w-8 h-8 border-3 border-border border-t-primary rounded-full animate-spin"></div>
                                            </div>
                                        ) : addresses.length === 0 && !showAddressForm ? (
                                            <div className="text-center py-12 bg-card rounded-lg border border-border">
                                                <i className="ri-map-pin-line text-4xl text-muted-foreground/40 mb-3 block"></i>
                                                <p className="text-muted-foreground mb-4">No saved addresses found</p>
                                                <button onClick={() => setShowAddressForm(true)} className="px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:brightness-110 transition-all">
                                                    Add New Address
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {addresses.map(addr => (
                                                    <AddressCard
                                                        key={addr._id}
                                                        address={addr}
                                                        isSelected={selectedAddressId === addr._id}
                                                        onSelect={() => { setSelectedAddressId(addr._id); setSelectedAddress(addr); }}
                                                        selectable
                                                        showActions={false}
                                                    />
                                                ))}
                                            </div>
                                        )}

                                        <button
                                            onClick={handleContinueToFulfillmentStep}
                                            disabled={!selectedAddressId}
                                            className="mt-6 px-8 py-3 bg-primary text-white rounded-lg font-semibold hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            Continue <i className="ri-arrow-right-line"></i>
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <h2 className="text-lg font-bold text-foreground mb-4">Your Details</h2>
                                        <div className="bg-card rounded-lg border border-border p-6 mb-6">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {[
                                                    { field: 'firstName', label: 'First Name', placeholder: 'John', type: 'text' },
                                                    { field: 'lastName', label: 'Last Name', placeholder: 'Doe', type: 'text' },
                                                    { field: 'email', label: 'Email', placeholder: 'john@example.com', type: 'email' },
                                                    { field: 'phone', label: 'Phone', placeholder: KENYA_PHONE_PLACEHOLDER, type: 'tel' },
                                                ].map(({ field, label, placeholder, type }) => (
                                                    <div key={field}>
                                                        <label className="block text-sm font-medium text-foreground mb-1">
                                                            {label} <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type={type}
                                                            value={guestDetails[field]}
                                                            onChange={(e) => handleGuestDetailChange(field, e.target.value)}
                                                            placeholder={placeholder}
                                                            className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${guestErrors[field] ? 'border-red-500' : 'border-border focus:border-primary'}`}
                                                        />
                                                        {guestErrors[field] && <p className="text-red-500 text-xs mt-1">{guestErrors[field]}</p>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <h2 className="text-lg font-bold text-foreground mb-4">Shipping Address</h2>
                                        {(guestAddress && !editingGuestAddress) ? (
                                            <div className="mb-4 bg-card rounded-lg border-2 border-primary p-4">
                                                <div className="flex items-start justify-between">
                                                    <div className="space-y-1">
                                                        <p className="text-sm font-semibold text-foreground">{guestAddress.fullName}</p>
                                                        <p className="text-sm text-muted-foreground">{guestAddress.phone}</p>
                                                        <p className="text-sm text-muted-foreground">{guestAddress.addressLine1}</p>
                                                        {guestAddress.addressLine2 && <p className="text-sm text-muted-foreground">{guestAddress.addressLine2}</p>}
                                                        <p className="text-sm text-muted-foreground">Postal Code: {guestAddress.postalCode}</p>
                                                    </div>
                                                    <button onClick={() => setEditingGuestAddress(true)} className="text-sm text-primary hover:underline">Edit</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <AddressForm
                                                initialData={editingGuestAddress ? {
                                                    fullName: guestAddress?.fullName || '',
                                                    phone: guestAddress?.phone || '',
                                                    addressLine1: guestAddress?.addressLine1 || '',
                                                    addressLine2: guestAddress?.addressLine2 || '',
                                                    country: guestAddress?.countryId || '',
                                                    state: guestAddress?.stateId || '',
                                                    city: guestAddress?.cityId || '',
                                                    postalCode: guestAddress?.postalCode || '',
                                                } : undefined}
                                                contactPrefill={{
                                                    fullName: `${guestDetails.firstName} ${guestDetails.lastName}`.trim(),
                                                    phone: guestDetails.phone,
                                                }}
                                                showUseSameAsAbove
                                                onSubmit={handleGuestAddressSubmit}
                                                onCancel={editingGuestAddress ? () => setEditingGuestAddress(false) : undefined}
                                                submitLabel={editingGuestAddress ? 'Update Address' : 'Use This Address'}
                                                isLoading={false}
                                            />
                                        )}

                                        <button
                                            onClick={handleContinueToFulfillmentStep}
                                            disabled={!guestAddress || editingGuestAddress}
                                            className="mt-6 px-8 py-3 bg-primary text-white rounded-lg font-semibold hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            Continue <i className="ri-arrow-right-line"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── STEP 2: Fulfillment ─────────────────────────── */}
                        {currentStep === 2 && (
                            <div>
                                <button onClick={() => setCurrentStep(1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-4">
                                    <i className="ri-arrow-left-line"></i> Back
                                </button>
                                <h2 className="text-lg font-bold text-foreground mb-4">How should we fulfill your order?</h2>

                                {/* Fulfillment type selector */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                                    {[
                                        { id: 'delivery', label: 'Deliver to My Address', icon: '🚚', sublabel: 'We deliver to the address you provided' },
                                        { id: 'store_pickup', label: 'Pick Up from Store', icon: '🏪', sublabel: 'Collect your order from a nearby store' },
                                    ].map((ft) => (
                                        <div
                                            key={ft.id}
                                            onClick={() => {
                                                setFulfillmentType(ft.id);
                                                if (ft.id === 'store_pickup') {
                                                    loadNearbyStores();
                                                    if (!userLocation) handleRequestGeolocation();
                                                }
                                            }}
                                            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${fulfillmentType === ft.id ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-border bg-card hover:border-primary/50'}`}
                                        >
                                            <div className="text-3xl mb-2">{ft.icon}</div>
                                            <div className="font-semibold text-foreground">{ft.label}</div>
                                            <div className="text-xs text-muted-foreground mt-1">{ft.sublabel}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* ── Delivery sub-options ── */}
                                {fulfillmentType === 'delivery' && (
                                    <div className="bg-card rounded-xl border border-border p-4 mb-6">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="text-sm font-semibold text-foreground">Delivery Provider</h3>
                                            <button
                                                onClick={handleRequestGeolocation}
                                                className="flex items-center gap-1 text-xs text-primary hover:underline"
                                            >
                                                <i className="ri-focus-3-line"></i> Use my current location
                                            </button>
                                        </div>
                                        {providerLoading ? (
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin"></div>
                                                Calculating delivery options...
                                            </div>
                                        ) : (
                                            <>
                                                {distanceKm != null && littleCabEnabled && (
                                                    <p className="text-xs text-muted-foreground mb-3">
                                                        Your address is approximately <strong>{distanceKm} km</strong> from our fulfillment center.
                                                        {distanceKm <= radiusKm
                                                            ? ' Little Cab same-day delivery is available.'
                                                            : ` Little Cab is only available within ${radiusKm} km.`}
                                                    </p>
                                                )}
                                                <DeliveryOptions
                                                    selected={deliveryProvider}
                                                    onSelect={setDeliveryProvider}
                                                    distanceKm={distanceKm}
                                                    radiusKm={radiusKm}
                                                    littleCabEnabled={littleCabEnabled}
                                                    fargoEnabled={fargoEnabled}
                                                    manualDeliveryEnabled={manualDeliveryEnabled}
                                                />

                                                {deliveryProvider === 'littlecab' && userLocation && warehouseLocation && (
                                                    <div className="mt-4 pt-4 border-t border-border/50">
                                                        <h4 className="text-xs font-bold text-foreground mb-2 uppercase tracking-wider">Delivery Coverage Map</h4>
                                                        <DeliveryRangeMap 
                                                            userLocation={userLocation} 
                                                            warehouseLocation={warehouseLocation} 
                                                            radiusKm={radiusKm} 
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* ── Store pickup sub-options ── */}
                                {fulfillmentType === 'store_pickup' && (
                                    <div className="bg-card rounded-xl border border-border p-3 sm:p-4 mb-6">
                                        <div className="flex items-center justify-between gap-2 mb-2">
                                            <h3 className="text-sm font-semibold text-foreground">Select Pickup Store</h3>
                                            <button
                                                onClick={handleRequestGeolocation}
                                                className="flex items-center gap-1 text-xs text-primary hover:underline shrink-0"
                                            >
                                                <i className="ri-focus-3-line"></i> Use my location
                                            </button>
                                        </div>

                                        {selectedStore && (
                                            <div className="mb-2 flex items-start gap-2 rounded-lg border border-primary bg-primary/10 px-3 py-2">
                                                <i className="ri-checkbox-circle-fill text-primary text-lg shrink-0 mt-0.5"></i>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-[11px] font-semibold uppercase tracking-wide text-primary">
                                                        Selected store
                                                    </div>
                                                    <div className="text-sm font-semibold text-foreground truncate">
                                                        {selectedStore.storeName}
                                                    </div>
                                                    {(selectedStore.address || selectedStore.phone) && (
                                                        <div className="text-[11px] text-muted-foreground truncate">
                                                            {[selectedStore.address, selectedStore.phone].filter(Boolean).join(' · ')}
                                                        </div>
                                                    )}
                                                </div>
                                                {Number.isFinite(selectedStore.distanceKm) && (
                                                    <span className="text-[11px] font-bold text-primary shrink-0 tabular-nums">
                                                        {Number(selectedStore.distanceKm).toFixed(1)} km
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {storesLoading ? (
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
                                                <div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin"></div>
                                                Loading nearby stores...
                                            </div>
                                        ) : (
                                            <>
                                                <StoreLocatorMap
                                                    stores={nearbyStores}
                                                    userLocation={userLocation}
                                                    selectedStoreId={selectedStore?._id}
                                                    onSelectStore={handleSelectStore}
                                                    height={220}
                                                />

                                                {nearbyStores.length > 0 && (
                                                    <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
                                                        {nearbyStores.map(store => {
                                                            const openLabel =
                                                                store.hoursDisplay?.compactStatus ||
                                                                store.hoursDisplay?.todayStatus ||
                                                                '';
                                                            const isOpen = openLabel === 'Open';
                                                            const isSelected =
                                                                String(selectedStore?._id || '') === String(store._id || '');
                                                            return (
                                                                <button
                                                                    key={store._id}
                                                                    type="button"
                                                                    onClick={() => handleSelectStore(store)}
                                                                    aria-pressed={isSelected}
                                                                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left rounded-lg border-2 transition-all ${
                                                                        isSelected
                                                                            ? 'border-primary bg-primary/10 shadow-sm ring-1 ring-primary/30'
                                                                            : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30'
                                                                    }`}
                                                                >
                                                                    <span
                                                                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                                                            isSelected
                                                                                ? 'border-primary bg-primary text-white'
                                                                                : 'border-muted-foreground/40 bg-card'
                                                                        }`}
                                                                        aria-hidden
                                                                    >
                                                                        {isSelected && (
                                                                            <i className="ri-check-line text-[11px] leading-none"></i>
                                                                        )}
                                                                    </span>
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="flex items-center gap-2 min-w-0">
                                                                            <span className={`text-sm truncate ${isSelected ? 'font-bold text-foreground' : 'font-medium text-foreground'}`}>
                                                                                {store.storeName}
                                                                            </span>
                                                                            {isSelected && (
                                                                                <span className="text-[10px] font-bold uppercase tracking-wide text-primary shrink-0">
                                                                                    Selected
                                                                                </span>
                                                                            )}
                                                                            {openLabel && (
                                                                                <span
                                                                                    className={`text-[10px] font-semibold shrink-0 ${
                                                                                        isOpen
                                                                                            ? 'text-emerald-600'
                                                                                            : 'text-muted-foreground'
                                                                                    }`}
                                                                                >
                                                                                    {openLabel}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {(store.address || store.landmark) && (
                                                                            <div className="text-[11px] text-muted-foreground truncate">
                                                                                {store.address || store.landmark}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {Number.isFinite(store.distanceKm) && (
                                                                        <span className={`text-[11px] font-semibold shrink-0 tabular-nums ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                                                                            {Number(store.distanceKm).toFixed(1)} km
                                                                        </span>
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {!storesLoading && nearbyStores.length === 0 && (
                                                    <p className="text-sm text-muted-foreground text-center py-4">
                                                        {userLocation
                                                            ? 'No pickup stores found near your location. Enable “Appear in Pick Up from Store” for stores in Admin, or try another location.'
                                                            : 'Use your current location to see nearby pickup stores and distances.'}
                                                    </p>
                                                )}
                                            </>
                                        )}

                                        {selectedStore && !stockLoading && stockStatus.length > 0 && (
                                            <div className="mt-3">
                                                <TransitCooldownBanner items={stockStatus} />
                                            </div>
                                        )}
                                    </div>
                                )}

                                <button
                                    onClick={handleContinueToPaymentStep}
                                    className="px-8 py-3 bg-primary text-white rounded-lg font-semibold hover:brightness-110 transition-all flex items-center gap-2"
                                >
                                    Continue <i className="ri-arrow-right-line"></i>
                                </button>
                            </div>
                        )}

                        {/* ── STEP 3: Payment Method ──────────────────────── */}
                        {currentStep === 3 && (
                            <div>
                                <button onClick={() => setCurrentStep(2)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-4">
                                    <i className="ri-arrow-left-line"></i> Back
                                </button>
                                <h2 className="text-lg font-bold text-foreground mb-4">How would you like to pay?</h2>

                                <div className="space-y-3 mb-6">
                                    {VISIBLE_PAYMENT_METHODS.map((pm) => {
                                        const isLoyaltyEligible = loyaltyWallet?.eligiblePaymentMethods?.includes(pm.id);
                                        return (
                                            <label
                                                key={pm.id}
                                                className={`flex items-start gap-3 p-4 bg-card rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === pm.id ? 'border-primary ring-1 ring-primary/20' : 'border-border hover:border-border'}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="paymentMethod"
                                                    value={pm.id}
                                                    checked={paymentMethod === pm.id}
                                                    onChange={() => setPaymentMethod(pm.id)}
                                                    className="mt-0.5 accent-primary"
                                                />
                                                <span className="text-2xl">{pm.icon}</span>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-sm font-semibold text-foreground">{pm.label}</span>
                                                        {pm.badge && <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded font-medium">{pm.badge}</span>}
                                                        {!pm.isOnline && <span className="px-2 py-0.5 bg-orange-50 text-orange-700 text-xs rounded font-medium">No online payment</span>}
                                                        {isLoyaltyEligible && (
                                                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] uppercase tracking-wider rounded font-bold border border-amber-100 flex items-center gap-1">
                                                                <i className="ri-coin-line text-xs"></i> Earns Points
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-muted-foreground mt-1">{pm.sublabel}</p>
                                                    {(pm.id === 'visa' || pm.id === 'mastercard') && paymentMethod === pm.id && (
                                                        <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                                                            Bank integration in progress. Your order will be created and our team will contact you to complete payment.
                                                        </div>
                                                    )}
                                                    {pm.id === 'mosmos' && paymentMethod === pm.id && (
                                                        <div
                                                            className="mt-3 p-3 rounded border mosmos-period-box"
                                                            onClick={e => e.preventDefault()}
                                                        >
                                                            <label className="block text-xs font-semibold mb-1 mosmos-period-label">
                                                                Installment Period (Days) <span className="text-red-500">*</span>
                                                            </label>
                                                            <input 
                                                                type="number"
                                                                min="1"
                                                                value={mosmosPeriod}
                                                                onChange={(e) => setMosmosPeriod(e.target.value)}
                                                                placeholder="e.g. 90"
                                                                className="w-full sm:w-1/2 px-3 py-2 outline-none rounded text-sm mosmos-period-input"
                                                                onClick={e => e.stopPropagation()}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={handleSelectPaymentAndContinue}
                                    className="px-8 py-3 bg-primary text-white rounded-lg font-semibold hover:brightness-110 transition-all flex items-center gap-2"
                                >
                                    {paymentMethod === 'mosmos' ? 'Proceed' : 'Continue'} <i className="ri-arrow-right-line"></i>
                                </button>
                                
                                {/* Hidden Mosmos Button Mount Point */}
                                {paymentMethod === 'mosmos' && (
                                    <div id="mmBtn" style={{ visibility: 'hidden', height: 0, overflow: 'hidden' }}></div>
                                )}
                            </div>
                        )}

                        {/* ── STEP 4: Confirm & Place Order ──────────────── */}
                        {currentStep === 4 && (
                            <div>
                                <button onClick={() => setCurrentStep(3)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-4">
                                    <i className="ri-arrow-left-line"></i> Back
                                </button>

                                {/* Summary of selections */}
                                <div className="bg-card rounded-xl border border-border p-4 mb-6 space-y-3">
                                    <h3 className="font-semibold text-foreground">Order Summary</h3>
                                    <div className="flex items-center gap-2 text-sm">
                                        <i className="ri-bank-card-line text-primary"></i>
                                        <span className="text-muted-foreground">Payment:</span>
                                        <span className="font-medium text-foreground capitalize">{paymentMethod}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <i className={fulfillmentType === 'delivery' ? 'ri-truck-line text-primary' : 'ri-store-2-line text-primary'}></i>
                                        <span className="text-muted-foreground">Fulfillment:</span>
                                        <span className="font-medium text-foreground">
                                            {fulfillmentType === 'delivery'
                                                ? deliveryProvider === 'littlecab'
                                                    ? 'Delivery via Little Cab (Same-day)'
                                                    : deliveryProvider === 'fargo'
                                                      ? 'Delivery via Fargo Courier'
                                                      : 'Delivery'
                                                : `Pickup from ${selectedStore?.storeName}`}
                                        </span>
                                    </div>
                                </div>

                                {/* M-Pesa phone input */}
                                {paymentMethod === 'mpesa' && (
                                    <div className="bg-card rounded-xl border border-border p-4 mb-6">
                                        <label className="block text-sm font-medium text-foreground mb-1">M-PESA Phone Number</label>
                                        <input
                                            type="tel"
                                            value={mpesaPhone}
                                            onChange={(e) => setMpesaPhone(formatPhoneWithPrefix(e.target.value))}
                                            placeholder={KENYA_PHONE_PLACEHOLDER}
                                            className="w-full max-w-xs px-3 py-2.5 border border-border rounded-lg text-sm outline-none focus:border-primary transition-colors"
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">{KENYA_PHONE_HINT}</p>
                                    </div>
                                )}

                                {/* Store Credit */}
                                {isAuthenticated && customerStoreCredit > 0 && (
                                    <div className="mb-6 p-4 bg-card rounded-xl border border-border">
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input type="checkbox" checked={useStoreCredit} onChange={(e) => setUseStoreCredit(e.target.checked)} className="mt-0.5 accent-primary w-4 h-4" />
                                            <div>
                                                <span className="text-sm font-medium text-foreground">Apply Store Credit </span>
                                                <span className="text-sm text-muted-foreground">({formatKES(customerStoreCredit)} available)</span>
                                                {useStoreCredit && (
                                                    <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-700">
                                                        <p>Applied: <strong>{formatKES(appliedCredit)}</strong></p>
                                                        <p>Remaining to pay: <strong>{formatKES(remainingToPay)}</strong></p>
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    </div>
                                )}

                                {/* Loyalty Points Redemption */}
                                {isAuthenticated && loyaltyWallet?.isEnabled && loyaltyWallet?.available > 0 && (
                                    <div className="mb-6 p-4 bg-card rounded-xl border border-border shadow-sm">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                                                    <i className="ri-coin-line text-primary"></i>
                                                </div>
                                                <div>
                                                    <h3 className="text-sm font-bold text-foreground">Redeem Loyalty Points</h3>
                                                    <p className="text-xs text-muted-foreground">Available: <strong>{loyaltyWallet.available.toLocaleString()} points</strong></p>
                                                </div>
                                            </div>
                                            <Link to="/loyalty-program" target="_blank" className="text-[10px] text-muted-foreground hover:text-primary underline flex items-center gap-1">
                                                How it works <i className="ri-external-link-line"></i>
                                            </Link>
                                        </div>

                                        {loyaltyBlockedByCoupon ? (
                                            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                                                {loyaltyStackMode === 'block_if_coupon'
                                                    ? 'Remove the coupon to redeem loyalty points.'
                                                    : 'Only one discount is allowed — remove the coupon to use loyalty points.'}
                                            </p>
                                        ) : (
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2">
                                                <div className="relative flex-1 max-w-[240px]">
                                                    <input
                                                        type="number"
                                                        value={loyaltyPointsToRedeem || ''}
                                                        onChange={(e) => setLoyaltyPointsToRedeem(parseInt(e.target.value) || 0)}
                                                        placeholder="Enter points to use"
                                                        max={loyaltyWallet.available}
                                                        className="w-full pl-3 pr-20 py-2.5 border border-border rounded-lg text-sm focus:border-primary outline-none transition-all shadow-sm"
                                                    />
                                                    <button
                                                        onClick={() => setLoyaltyPointsToRedeem(loyaltyWallet.available)}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-primary hover:bg-primary/5 px-2 py-1 rounded transition-colors"
                                                    >
                                                        USE ALL
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={() => handleApplyLoyalty({ target: { value: loyaltyPointsToRedeem } })}
                                                    disabled={validatingLoyalty || loyaltyPointsToRedeem <= 0}
                                                    className="px-6 py-2.5 bg-foreground text-background rounded-lg text-sm font-bold hover:brightness-125 transition-all disabled:opacity-50 flex items-center gap-2"
                                                >
                                                    {validatingLoyalty ? (
                                                        <div className="w-4 h-4 border-2 border-background/30 border-t-background rounded-full animate-spin"></div>
                                                    ) : 'Apply'}
                                                </button>
                                            </div>

                                            {useLoyalty && loyaltyDiscount > 0 && (
                                                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-100 dark:border-green-900/50">
                                                    <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                                                        <i className="ri-checkbox-circle-fill"></i>
                                                        <span><strong>{formatKES(loyaltyDiscount)}</strong> discount applied</span>
                                                    </div>
                                                    <button 
                                                        onClick={() => {
                                                            setUseLoyalty(false);
                                                            setLoyaltyPointsToRedeem(0);
                                                            setLoyaltyDiscount(0);
                                                        }}
                                                        className="text-xs text-red-500 hover:underline"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        )}
                                    </div>
                                )}

                                {/* Order Notes */}
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-foreground mb-1">Order Notes (Optional)</label>
                                    <textarea
                                        value={orderNotes}
                                        onChange={(e) => setOrderNotes(e.target.value)}
                                        placeholder="Any special instructions..."
                                        rows={3}
                                        className="w-full px-3 py-2.5 border border-border rounded-lg text-sm outline-none focus:border-primary transition-colors resize-none"
                                    />
                                </div>
                                
                                <button
                                    onClick={handlePlaceOrder}
                                    disabled={placingOrder}
                                    className="w-full sm:w-auto px-10 py-3 bg-primary text-white rounded-lg font-semibold hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {placingOrder ? (
                                        <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Placing Order...</>
                                    ) : (
                                        <><i className="ri-lock-line"></i> Place Order ({formatKES(remainingToPay)})</>
                                    )}
                                </button>
                            </div>
                        )}

                        {/* ── STEP 5: Pay at Shop — Informational ─────────── */}
                        {currentStep === 5 && (
                            <div>
                                <button onClick={() => setCurrentStep(2)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-4">
                                    <i className="ri-arrow-left-line"></i> Back to Payment Options
                                </button>

                                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                                    <div className="flex items-start gap-3">
                                        <span className="text-2xl">🏪</span>
                                        <div>
                                            <h3 className="font-semibold text-orange-900 mb-1">Buy Directly at Our Store</h3>
                                            <p className="text-sm text-orange-800">
                                                You can walk into any of our stores below to purchase these items. Show this item list to our staff when you arrive.
                                            </p>
                                            <p className="text-xs text-orange-700 mt-2">
                                                <strong>Note:</strong> In-store stock is subject to availability and this does not reserve your items.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Cart items list to show at store */}
                                <div className="bg-card rounded-xl border border-border p-4 mb-6">
                                    <h3 className="font-semibold text-foreground mb-3">Items to Purchase</h3>
                                    <div className="space-y-3">
                                        {items.map((item) => {
                                            const imgSrc = resolveAssetUrl(
                                                item.variant?.image ||
                                                    item.variant?.variantImage?.find((i) => i.isPrimary)?.path ||
                                                    item.variant?.variantImage?.[0]?.path
                                            );
                                            return (
                                                <div key={item.variantId} className="flex gap-3 items-center">
                                                    <div className="w-12 h-12 bg-muted rounded flex-shrink-0 overflow-hidden">
                                                        {imgSrc ? (
                                                            <img src={imgSrc} alt={item.product?.name} className="w-full h-full object-contain p-1" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center"><i className="ri-image-line text-muted-foreground/40"></i></div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-foreground">{item.product?.name}</p>
                                                        <p className="text-xs text-muted-foreground">Qty: {item.quantity} · {item.variant?.label}</p>
                                                    </div>
                                                    <span className="text-sm font-semibold text-primary">{formatKES(item.pricing?.lineTotal)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="border-t border-border mt-3 pt-3 flex justify-between">
                                        <span className="font-semibold text-foreground">Total</span>
                                        <span className="font-bold text-primary">{formatKES(grandTotal)}</span>
                                    </div>
                                </div>

                                {/* Store locator map */}
                                <h3 className="font-semibold text-foreground mb-3">Find a Store Near You</h3>
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-sm text-muted-foreground">Click on a store pin to see details</p>
                                    <button onClick={handleRequestGeolocation} className="flex items-center gap-1 text-xs text-primary hover:underline">
                                        <i className="ri-focus-3-line"></i> Use my location
                                    </button>
                                </div>

                                {storesLoading ? (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
                                        <div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin"></div>
                                        Loading nearby stores...
                                    </div>
                                ) : (
                                    <StoreLocatorMap
                                        stores={nearbyStores}
                                        userLocation={userLocation}
                                        selectedStoreId={selectedStore?._id}
                                        onSelectStore={setSelectedStore}
                                    />
                                )}

                                {selectedStore && (
                                    <div className="mt-4 bg-card rounded-xl border-2 border-primary p-4">
                                        <div className="flex items-start gap-3">
                                            <i className="ri-store-2-line text-primary text-xl"></i>
                                            <div>
                                                <div className="font-semibold text-foreground">{selectedStore.storeName}</div>
                                                {selectedStore.address && <div className="text-sm text-muted-foreground">{selectedStore.address}</div>}
                                                {selectedStore.phone && <div className="text-sm text-muted-foreground">{selectedStore.phone}</div>}
                                                {selectedStore.hoursDisplay && (
                                                    <StoreHoursBlock hoursDisplay={selectedStore.hoursDisplay} className="mt-2" />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {nearbyStores.length === 0 && !storesLoading && (
                                    <p className="text-sm text-center text-muted-foreground mt-4">
                                        No stores found near your location. Please enable location access or visit our website for store addresses.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── Order Summary Sidebar ────────────────────────────── */}
                    {currentStep !== 5 && (
                        <div className="lg:col-span-1">
                            <div className="bg-card rounded-lg border border-border p-6 lg:sticky lg:top-[180px]">
                                <h2 className="text-lg font-bold text-foreground mb-4">Order Summary</h2>

                                <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                                    {items.map((item) => {
                                        const imgSrc = resolveAssetUrl(
                                            item.variant?.image ||
                                                item.variant?.variantImage?.find((i) => i.isPrimary)?.path ||
                                                item.variant?.variantImage?.[0]?.path
                                        );
                                        return (
                                            <div key={item.variantId} className="flex gap-3">
                                                <div className="w-12 h-12 bg-muted rounded flex-shrink-0 overflow-hidden">
                                                    {imgSrc ? (
                                                        <img src={imgSrc} alt={item.product?.name} className="w-full h-full object-contain p-1" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center"><i className="ri-image-line text-muted-foreground/40"></i></div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs text-foreground font-medium line-clamp-1">{item.product?.name}</p>
                                                    <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                                                </div>
                                                <span className="text-xs font-medium text-foreground flex-shrink-0">{formatKES(item.pricing?.lineTotal)}</span>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="border-t border-border pt-3 space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Subtotal ({cart?.itemCount || 0} items)</span>
                                        <span className="text-foreground font-medium">{formatKES(summary.subtotal)}</span>
                                    </div>
                                    {(summary.vatAddedAmount ?? 0) > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">VAT ({summary.vatRate ?? vatRate}%) on eligible items</span>
                                            <span className="text-foreground font-medium">{formatKES(summary.vatAddedAmount)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Shipping</span>
                                        <span className={`font-medium ${summary.shippingCharge === 0 ? 'text-green-600' : 'text-foreground'}`}>
                                            {fulfillmentType === 'store_pickup' ? 'Free (Pickup)' : summary.shippingCharge === 0 ? 'Free' : formatKES(summary.shippingCharge)}
                                        </span>
                                    </div>
                                    {summary.couponDiscount > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-green-600">Discount ({cart.couponCode})</span>
                                            <span className="text-green-600 font-medium">-{formatKES(summary.couponDiscount)}</span>
                                        </div>
                                    )}
                                    <div className="pt-1">
                                        <CouponInput
                                            disabled={couponBlockedByLoyalty}
                                            disabledHint="Remove loyalty points to apply a coupon (only one discount allowed)"
                                        />
                                    </div>
                                    {useLoyalty && appliedLoyaltyDiscount > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-green-600">Loyalty Discount</span>
                                            <span className="text-green-600 font-medium">-{formatKES(appliedLoyaltyDiscount)}</span>
                                        </div>
                                    )}
                                    {useStoreCredit && appliedCredit > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-green-600">Store Credit</span>
                                            <span className="text-green-600 font-medium">-{formatKES(appliedCredit)}</span>
                                        </div>
                                    )}
                                    <div className="border-t border-border pt-2 mt-2">
                                        <div className="flex justify-between">
                                            <span className="text-base font-bold text-foreground">Total</span>
                                            <span className="text-lg font-bold text-primary">
                                                {formatKES(remainingToPay)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {cart?.giftEvaluation && (
                                    <CartGiftProgress
                                        giftEvaluation={cart.giftEvaluation}
                                        onOpenGiftModal={(slab) => setGiftModalSlab(slab)}
                                        onGiftRemoved={() => refreshCart?.()}
                                    />
                                )}

                                {boughtTogetherItems.length > 0 && (
                                    <div className="mt-4">
                                        <BoughtTogetherSection
                                            items={boughtTogetherItems}
                                            onAddToCart={addToCart}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Free Gift Selection Modal */}
            {giftModalSlab && (
                <GiftSelectionModal
                    currentSlab={giftModalSlab}
                    selectedGifts={cart?.giftEvaluation?.selectedGifts || []}
                    onClose={() => setGiftModalSlab(null)}
                    onGiftSelected={() => { setGiftModalSlab(null); refreshCart?.(); }}
                />
            )}

            {/* Guest: create account or continue */}
            {showGuestAccountModal && (
                <GuestCheckoutAccountModal
                    open
                    email={guestDetails.email}
                    onClose={() => setShowGuestAccountModal(false)}
                    onContinueAsGuest={handleContinueAsGuest}
                    onCreateAccount={handleCreateAccountFromCheckout}
                    isSubmitting={creatingGuestAccount}
                />
            )}

            {/* Payment Confirmation Modal (M-Pesa) */}
            {showPaymentModal && (
                <PaymentConfirmationModal
                    status={paymentStatus}
                    order={currentOrder}
                    mpesaPhone={mpesaPhone ? String(mpesaPhone).slice(-4) : ''}
                    onClose={handleClosePaymentModal}
                />
            )}

            {/* Pesapal Payment Iframe Modal */}
            {showPesapalIframe && pesapalRedirectUrl && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl shadow-2xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/50">
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">🔒</span>
                                <div>
                                    <h3 className="text-base font-bold text-foreground">Secure Payment via Pesapal</h3>
                                    <p className="text-xs text-muted-foreground">Complete your payment below. Do not close this window.</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowPesapalIframe(false);
                                    setPesapalRedirectUrl(null);
                                    if (socket && currentOrder) socket.emit('leave:order', { orderNumber: currentOrder.orderNumber });
                                    toast('Payment cancelled. Your order is still pending.', { icon: '⚠️' });
                                }}
                                className="p-2 hover:bg-muted rounded-lg transition-colors"
                                title="Cancel Payment"
                            >
                                <i className="ri-close-line text-xl text-muted-foreground"></i>
                            </button>
                        </div>
                        {/* Iframe */}
                        <div className="flex-1 bg-card">
                            <iframe
                                src={pesapalRedirectUrl}
                                title="Pesapal Payment"
                                className="w-full h-full border-0"
                                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
